import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const EMPTY_VIS = { division_ids: [], profile_ids: [], user_ids: [] };

function useOptions() {
  const [o, setO] = useState({ divisions: [], profiles: [], users: [] });
  useEffect(() => {
    const get = (p) => api.get(p).then((r) => r.data).catch(() => []);
    Promise.all([get("/r/divisions"), get("/access-profiles"), get("/users")]).then(([d, p, u]) => setO({ divisions: d, profiles: p, users: u }));
  }, []);
  return o;
}

function Checks({ label, items, value, onChange, tid }) {
  if (!items.length) return null;
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div><div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">{items.map(([v, l]) => (
        <button type="button" key={v} onClick={() => toggle(v)} data-testid={`${tid}-${v}`}
          className={`px-2 py-1 rounded-md text-xs border transition-colors ${value.includes(v) ? "border-[#C2A462] bg-[#C2A462]/15 text-[#EBD6A0]" : "border-white/10 text-slate-400"}`}>{l}</button>
      ))}</div></div>
  );
}

function VisibilityPicker({ value, onChange }) {
  const o = useOptions();
  const v = { ...EMPTY_VIS, ...(value || {}) };
  const set = (k, arr) => onChange({ ...v, [k]: arr });
  const all = !v.division_ids.length && !v.profile_ids.length && !v.user_ids.length;
  return (
    <div className="space-y-2 rounded-md border border-white/10 p-3" data-testid="link-visibility">
      <div className="text-xs font-semibold">Who can see this? <span className="font-normal text-muted-foreground">{all ? "Everyone in this organisation" : "Only the selected divisions, access profiles or people"}</span></div>
      <Checks label="Divisions" items={o.divisions.map((d) => [d.name, d.name])} value={v.division_ids} onChange={(a) => set("division_ids", a)} tid="vis-division" />
      <Checks label="Access profiles" items={o.profiles.map((p) => [p.id, p.name])} value={v.profile_ids} onChange={(a) => set("profile_ids", a)} tid="vis-profile" />
      <Checks label="People" items={o.users.filter((u) => !u.disabled).map((u) => [u.id, u.name])} value={v.user_ids} onChange={(a) => set("user_ids", a)} tid="vis-user" />
    </div>
  );
}

function Shell({ title, onClose, children, onSave, busy }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>
        <div className="space-y-3">{children}</div>
        <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={onSave} data-testid="link-dialog-save">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => <label className="block text-xs text-muted-foreground space-y-1"><span>{label}</span>{children}</label>;

export function CategoryDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", description: "", display_order: 0, is_active: true, visibility: EMPTY_VIS, ...initial });
  const [busy, setBusy] = useState(false);
  const personal = f.scope === "personal";
  const save = async () => {
    setBusy(true);
    const body = { name: f.name, description: f.description || "", display_order: Number(f.display_order) || 0, is_active: f.is_active, scope: f.scope, visibility: f.visibility || EMPTY_VIS };
    try { f.id ? await api.put(`/links/categories/${f.id}`, body) : await api.post("/links/categories", body); toast.success("Category saved"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell title={`${f.id ? "Edit" : "New"} ${personal ? "personal " : ""}category`} onClose={onClose} onSave={save} busy={busy}>
      <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cat-name" /></Field>
      <Field label="Description (optional)"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="cat-description" /></Field>
      <div className="flex gap-3">
        <Field label="Display order"><Input type="number" value={f.display_order} onChange={(e) => setF({ ...f, display_order: e.target.value })} data-testid="cat-order" /></Field>
        <label className="flex items-center gap-2 text-sm pt-5"><input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} data-testid="cat-active" />Active</label>
      </div>
      {!personal && <VisibilityPicker value={f.visibility} onChange={(v) => setF({ ...f, visibility: v })} />}
    </Shell>
  );
}

const NEW_CAT = "__new__";

export function LinkDialog({ initial, categories, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", url: "https://", description: "", display_order: 0, is_active: true, visibility: EMPTY_VIS, ...initial,
    category_id: initial.category_id || categories[0]?.id || NEW_CAT, keywords: (initial.search_keywords || []).join(", ") });
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);
  const personal = f._scope === "personal";
  const save = async () => {
    if (f.category_id === NEW_CAT && !newCat.trim()) return toast.error("Enter a name for the new category");
    setBusy(true);
    let category_id = f.category_id;
    try {
      if (category_id === NEW_CAT) category_id = (await api.post("/links/categories", { name: newCat.trim(), description: "", display_order: 0, is_active: true, scope: f._scope, visibility: EMPTY_VIS })).data.id;
    } catch (e) { setBusy(false); return toast.error(errMsg(e)); }
    const body = { category_id, title: f.title, url: f.url, description: f.description || "", display_order: Number(f.display_order) || 0,
      is_active: f.is_active, search_keywords: f.keywords.split(",").map((k) => k.trim()).filter(Boolean), visibility: f._scope === "personal" ? EMPTY_VIS : f.visibility || EMPTY_VIS };
    try { f.id ? await api.put(`/links/items/${f.id}`, body) : await api.post("/links/items", body); toast.success("Link saved"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell title={`${f.id ? "Edit" : "Add"} ${personal ? "private " : "organisation "}link`} onClose={onClose} onSave={save} busy={busy}>
      <Field label="Link title"><Input value={f.title} placeholder="e.g. ATO Business Portal" onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="link-title" /></Field>
      <Field label="Web address (URL) — must start with https://"><Input type="url" value={f.url} placeholder="https://www.example.com.au" onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="link-url" /></Field>
      <Field label="Category"><select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="link-category">
        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        <option value={NEW_CAT}>+ Create a new category…</option></select></Field>
      {f.category_id === NEW_CAT && <Field label="New category name"><Input value={newCat} placeholder="e.g. Government portals" onChange={(e) => setNewCat(e.target.value)} data-testid="link-new-category-name" /></Field>}
      <Field label="Description (optional)"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="link-description" /></Field>
      <Field label="Search keywords (comma separated, e.g. ATO, tax, BAS, GST)"><Input value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} data-testid="link-keywords" /></Field>
      <div className="flex gap-3">
        <Field label="Display order"><Input type="number" value={f.display_order} onChange={(e) => setF({ ...f, display_order: e.target.value })} data-testid="link-order" /></Field>
        <label className="flex items-center gap-2 text-sm pt-5"><input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} data-testid="link-active" />Active</label>
      </div>
      {!personal && <VisibilityPicker value={f.visibility} onChange={(v) => setF({ ...f, visibility: v })} />}
    </Shell>
  );
}
